import React, { useState, useEffect, useRef, useCallback } from 'react';
import { base44 } from '@/api/base44Client';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Upload, Crop, Trash2, AlertTriangle, Check } from 'lucide-react';
import { toast } from 'sonner';

const MAX_SIZE_BYTES = 200 * 1024; // 200KB

function ImageCropper({ src, onCropped, onCancel, onSkip, aspectRatio, minWidth }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [crop, setCrop] = useState({ x: 0, y: 0, w: 100, h: 100 }); // percent
  const [dragging, setDragging] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [processing, setProcessing] = useState(false);

  const getRelativePos = (e, el) => {
    const rect = el.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    };
  };

  const handleMouseDown = (e, handle) => {
    e.preventDefault();
    setDragging(handle);
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging || !imgRef.current) return;
    const pos = getRelativePos(e, imgRef.current);
    setCrop(prev => {
      let next = { ...prev };
      const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
      if (dragging === 'move') {
        next.x = clamp(pos.x - prev.w / 2, 0, 100 - prev.w);
        next.y = clamp(pos.y - prev.h / 2, 0, 100 - prev.h);
      } else if (!aspectRatio && dragging === 'se') {
        next.w = clamp(pos.x - prev.x, 10, 100 - prev.x);
        next.h = clamp(pos.y - prev.y, 10, 100 - prev.y);
      } else if (!aspectRatio && dragging === 'nw') {
        const newX = clamp(pos.x, 0, prev.x + prev.w - 10);
        const newY = clamp(pos.y, 0, prev.y + prev.h - 10);
        next.w = prev.x + prev.w - newX;
        next.h = prev.y + prev.h - newY;
        next.x = newX;
        next.y = newY;
      }
      return next;
    });
  }, [aspectRatio, dragging]);

  const handleImageLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    if (aspectRatio) {
      const imageRatio = img.naturalWidth / img.naturalHeight;
      if (imageRatio > aspectRatio) {
        const width = (aspectRatio / imageRatio) * 100;
        setCrop({ x: (100 - width) / 2, y: 0, w: width, h: 100 });
      } else {
        const height = (imageRatio / aspectRatio) * 100;
        setCrop({ x: 0, y: (100 - height) / 2, w: 100, h: height });
      }
    }
    setImgLoaded(true);
  };

  const handleMouseUp = () => setDragging(null);

  const applyCrop = async () => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth) {
      toast.error("Immagine non ancora pronta, attendi un istante");
      return;
    }
    setProcessing(true);
    try {
      const canvas = document.createElement('canvas');
      const naturalW = img.naturalWidth;
      const naturalH = img.naturalHeight;
      const sw = Math.max(1, Math.round((crop.w / 100) * naturalW));
      const sh = Math.max(1, Math.round((crop.h / 100) * naturalH));
      const sx = Math.round((crop.x / 100) * naturalW);
      const sy = Math.round((crop.y / 100) * naturalH);
      if (minWidth && sw < minWidth) {
        toast.error(`Il ritaglio deve essere largo almeno ${minWidth}px`);
        setProcessing(false);
        return;
      }
      canvas.width = sw;
      canvas.height = sh;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

      const blob = await new Promise((resolve, reject) => {
        try {
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('Blob vuoto'))),
            'image/jpeg',
            0.85
          );
        } catch (err) {
          reject(err);
        }
      });

      await onCropped(blob);
    } catch (err) {
      toast.error(`Ritaglio non riuscito: ${err?.message || 'errore sconosciuto'}`);
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/70 p-4">
      <div className="flex max-h-[calc(100dvh-2rem)] w-full max-w-2xl flex-col rounded-lg bg-white p-4">
        <h3 className="mb-3 shrink-0 font-medium">Ritaglia immagine</h3>
        <div
          className="relative mx-auto min-h-0 w-fit max-w-full select-none overflow-hidden rounded"
          style={{ userSelect: 'none' }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <img
            ref={imgRef}
            src={src}
            alt="crop"
            className="block max-h-[calc(100dvh-11rem)] max-w-full object-contain"
            crossOrigin={src.startsWith('blob:') ? undefined : 'anonymous'}
            onLoad={handleImageLoad}
            onError={() => toast.error("Impossibile caricare l'immagine per il ritaglio")}
            draggable={false}
          />
          {imgLoaded && (
            <>
              {/* Dark overlay outside crop */}
              <div className="absolute inset-0 pointer-events-none" style={{
                background: `rgba(0,0,0,0.5)`,
                WebkitMaskImage: `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25'><rect width='100%25' height='100%25' fill='white'/><rect x='${crop.x}%25' y='${crop.y}%25' width='${crop.w}%25' height='${crop.h}%25' fill='black'/></svg>")`,
              }} />
              {/* Crop box */}
              <div
                className="absolute border-2 border-white cursor-move"
                style={{ left: `${crop.x}%`, top: `${crop.y}%`, width: `${crop.w}%`, height: `${crop.h}%` }}
                onMouseDown={(e) => handleMouseDown(e, 'move')}
              >
                {!aspectRatio && <>
                  <div
                    className="absolute -top-2 -left-2 w-4 h-4 bg-white border border-gray-400 cursor-nw-resize"
                    onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, 'nw'); }}
                  />
                  <div
                    className="absolute -bottom-2 -right-2 w-4 h-4 bg-white border border-gray-400 cursor-se-resize"
                    onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, 'se'); }}
                  />
                </>}
              </div>
            </>
          )}
        </div>
        <div className="flex gap-2 mt-4 justify-end">
          <Button type="button" variant="outline" onClick={onCancel} disabled={processing}>Annulla</Button>
          {onSkip && (
            <Button type="button" variant="outline" onClick={onSkip} disabled={processing}>Carica senza ritagliare</Button>
          )}
          <Button type="button" onClick={applyCrop} disabled={processing || !imgLoaded}>
            <Check className="w-4 h-4 mr-1" /> {processing ? 'Elaborazione...' : 'Applica Ritaglio'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ImageUploadWithMeta({ value, meta, onChange, onMetaChange, onUploadingChange, label, fieldId, minWidth, aspectRatio }) {
  const [uploading, setUploading] = useState(false);
  const [fileSize, setFileSize] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [localMeta, setLocalMeta] = useState(meta || {});
  const aspectRatioLabel = aspectRatio === 3 / 2 ? '3:2' : aspectRatio === 16 / 9 ? '16:9' : '';

  // Sync localMeta when parent updates meta (e.g. from AI suggestions)
  // Use JSON.stringify to do deep comparison since meta is a new object every render
  const metaKey = JSON.stringify(meta);
  React.useEffect(() => {
    setLocalMeta(meta || {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaKey]);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    onUploadingChange?.(true);
    setFileSize(file.size);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      onChange(file_url);
      toast.success('Immagine caricata — ora puoi salvare il progetto');
    } catch {
      toast.error('Errore nel caricamento');
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileSize(file.size);
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const usableWidth = aspectRatio && image.width / image.height > aspectRatio
        ? image.height * aspectRatio
        : image.width;
      if (minWidth && usableWidth < minWidth) {
        toast.error(`Serve un'immagine che consenta un ritaglio largo almeno ${minWidth}px`);
        URL.revokeObjectURL(url);
        return;
      }
      setCropSrc(url);
      setPendingFile(file);
    };
    image.onerror = () => toast.error('Impossibile leggere le dimensioni dell’immagine');
    image.src = url;
    e.target.value = '';
  };

  const handleCropped = async (blob) => {
    setCropSrc(null);
    const croppedFile = new File([blob], pendingFile?.name || 'image.jpg', { type: 'image/jpeg' });
    setPendingFile(null);
    await uploadFile(croppedFile);
  };

  const handleSkipCrop = async () => {
    setCropSrc(null);
    await uploadFile(pendingFile);
    setPendingFile(null);
  };

  const overLimit = fileSize && fileSize > MAX_SIZE_BYTES;
  const excessKB = overLimit ? Math.round((fileSize - MAX_SIZE_BYTES) / 1024) : 0;

  const updateMeta = (key, val) => {
    const updated = { ...localMeta, [key]: val };
    setLocalMeta(updated);
    onMetaChange(updated);
  };

  return (
    <div className="border border-[#e5e5e5] rounded-lg p-4 space-y-3">
      <Label className="font-medium">{label}</Label>

      {(minWidth || aspectRatio) && <p className="text-xs text-[#7a7a7a]">Requisiti: larghezza minima {minWidth}px{aspectRatioLabel && `, formato ${aspectRatioLabel}`}.</p>}

      {/* Upload area */}
      <div className="flex gap-2 items-center">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => document.getElementById(`img-upload-${fieldId}`).click()}
        >
          <Upload className="w-4 h-4 mr-1" />
          {uploading ? 'Caricamento...' : value ? 'Sostituisci' : 'Carica immagine'}
        </Button>
        {value && (
          <Button type="button" variant="ghost" size="sm" onClick={() => { onChange(''); setFileSize(null); }}>
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        )}
        <input
          id={`img-upload-${fieldId}`}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
        />
      </div>

      {/* Size warning */}
      {fileSize && (
        <div className={`flex items-center gap-2 text-sm rounded px-3 py-2 ${overLimit ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {overLimit ? (
            <>
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>Peso: {Math.round(fileSize / 1024)} KB — <strong>{excessKB} KB in eccesso</strong> rispetto al limite di 200 KB. Carica un'immagine più leggera o usa il ritaglio.</span>
            </>
          ) : (
            <>
              <Check className="w-4 h-4 shrink-0" />
              <span>Peso: {Math.round(fileSize / 1024)} KB — OK</span>
            </>
          )}
        </div>
      )}

      {/* Preview */}
      {value && (
        <div className="relative group">
          <img src={value} alt={localMeta?.title || ''} className="h-40 w-full object-cover rounded" />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => { setCropSrc(value); setPendingFile(null); }}
          >
            <Crop className="w-4 h-4 mr-1" /> Ritaglia
          </Button>
        </div>
      )}

      {/* SEO Meta fields */}
      <div className="grid grid-cols-1 gap-2">
        <div>
          <Label className="text-xs text-[#7a7a7a]">Titolo immagine (alt text SEO)</Label>
          <Input
            value={localMeta?.title || ''}
            onChange={(e) => updateMeta('title', e.target.value)}
            placeholder="Es: Soggiorno moderno con pavimento in marmo"
          />
        </div>
        <div>
          <Label className="text-xs text-[#7a7a7a]">Descrizione immagine</Label>
          <Textarea
            value={localMeta?.description || ''}
            onChange={(e) => updateMeta('description', e.target.value)}
            rows={2}
            placeholder="Descrizione dettagliata per i motori di ricerca..."
          />
        </div>
        <div>
          <Label className="text-xs text-[#7a7a7a]">Tag / parole chiave (separate da virgola)</Label>
          <Input
            value={localMeta?.tags || ''}
            onChange={(e) => updateMeta('tags', e.target.value)}
            placeholder="Es: soggiorno, marmo, design minimalista, Palermo"
          />
        </div>
      </div>

      {/* Crop modal */}
      {cropSrc && (
        <ImageCropper
          src={cropSrc}
          onCropped={handleCropped}
          onSkip={pendingFile && !aspectRatio ? handleSkipCrop : null}
          aspectRatio={aspectRatio}
          minWidth={minWidth}
          onCancel={() => { setCropSrc(null); setPendingFile(null); }}
        />
      )}
    </div>
  );
}